import { useCallback, useMemo } from 'react';
import { AccommodationCombined, AccommodationCombinedType, AccommodationSourceType } from '../components/Accommodations/Accommodation.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCustomPageTelemetry } from '../../../Custom.telemetry';
import { useTranslation } from '@shared/core';
import { SearchParams, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useIncrementInterestedGuestCountMutation } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { EventVenue } from '@shared/utils/eventPlace';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useAccommodationsPrices } from './useAccommodationsPrices';
import { ButtonVariant } from '@withjoy/joykit/components/ButtonV2/Button.types';
import { hasAccommodationLink } from '../components/Accommodations/Accommodation.utils';
import { JoyHotelQueryStringParameters, useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { stripDefaultProperties } from '@shared/utils/stripDefaultProperties';
import { applyUtmParametersToUrl, useUtmParameters } from '@shared/utils/utmParameters';

interface AccommodationsCtaArgs
  extends Readonly<{
    accommodation?: AccommodationCombined;
    locationInfo?: EventVenue;
    from?: AccommodationSourceType;
    searchParams: SearchParams;
    onSoldOutItemClickAction?: () => void;
  }> {}

/**
 * Custom hook that provides CTA (Call-to-Action) configuration and handlers for accommodation tiles.
 *
 * This hook handles the logic for displaying and interacting with accommodation CTAs, including:
 * - Determining the appropriate button text based on accommodation type and availability
 * - Generating booking/hotel URLs with proper query parameters and discount codes
 * - Tracking telemetry events for user interactions
 * - Handling different accommodation types (Hotel, Roomblock, Custom)
 * - Managing sold-out hotel states and fallback behaviors
 *
 * @param {AccommodationsCtaArgs} props - Configuration object for the accommodation CTA
 * @param {AccommodationCombined} [props.accommodation] - The accommodation data including type, pricing, and booking information
 * @param {EventVenue} [props.locationInfo] - Event venue location data used for distance calculations and telemetry
 * @param {AccommodationSourceType} [props.from] - Source/context where the accommodation is displayed (for telemetry tracking)
 * @param {SearchParams} props.searchParams - Search parameters to include in booking URLs (dates, guests, etc.)
 * @param {() => void} [props.onSoldOutItemClickAction] - Optional callback fired when sold-out items are clicked (requires feature flag)
 *
 * @returns {Object} CTA configuration and handlers
 * @returns {string} returns.ctaTitle - The text to display on the CTA button. Varies by:
 *   - Sold out Joy Stays: "Explore Hotels Nearby"
 *   - Joy Stays with experiment: Uses experiment button text
 *   - Custom accommodations: "Select" (translated)
 *   - Default: "See Availability" (translated)
 * @returns {string} returns.ctaUrl - The URL to navigate to when CTA is clicked. Includes:
 *   - Discount codes/parameters if available
 *   - Search parameters (dates, guests)
 *   - Roomblock-specific paths for roomblocks
 *   - Sold-out hotel alternative URLs when applicable
 * @returns {ButtonVariant} returns.ctaVariant - Button style variant: 'outline' for sold-out hotels, 'solid' otherwise
 * @returns {boolean} returns.isLinkEnabled - Whether the CTA link should be enabled. Disabled for:
 *   - Sold-out hotels when accommodations console feature is enabled
 *   - Accommodations without valid booking/hotel links
 * @returns {boolean} returns.shouldShowButton - Whether the CTA button should be rendered at all. False when:
 *   - No valid hotel or booking link exists
 * @returns {Function} returns.handleOnSelectAccommodation - Click handler that:
 *   - Routes to appropriate handler based on accommodation type (Roomblock vs Hotel)
 *   - Tracks telemetry events with hotel info, pricing, distance, and source
 *   - Increments interested guest count for roomblocks
 * @returns {Function} returns.onSoldOutItemClick - Handler for sold-out items that:
 *   - Only active when sold-out URL exists and accommodations console feature is enabled
 *   - Invokes the optional onSoldOutItemClickAction callback
 */
export const useAccommodationsCta = (
  props: AccommodationsCtaArgs,
): {
  ctaTitle: string;
  ctaUrl: string;
  ctaVariant: ButtonVariant;
  isLinkEnabled: boolean;
  shouldShowButton: boolean;
  handleOnSelectAccommodation: (source?: string) => void;
  onSoldOutItemClick: () => void;
} => {
  const { accommodation, locationInfo, from, searchParams, onSoldOutItemClickAction } = props;

  const utmParamsOnly = {
    utm_medium: searchParams.utm_medium,
    utm_source: searchParams.utm_source,
    utm_campaign: searchParams.utm_campaign,
  };
  // Merge UTM params (existing URL UTM takes precedence)
  const mergedUtmParams = useUtmParameters(utmParamsOnly);

  const { eventInfo, eventHandle } = useEventInfo();
  const telemetry = useCustomPageTelemetry();
  const [incrementInterestedGuestCount] = useIncrementInterestedGuestCountMutation();
  const [isAccommodationsConsoleEnabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');
  const hotelRoutePaths = useHotelRoutePaths();
  const joyHotelDiscount = useHotelDiscount();

  const { tq } = useTranslation('guestSiteCustom');
  const isEnglish = tq`See Availability` === 'See Availability';
  const { payload } = useFeatureValue('accommodationsGuestHotelTileExperiment', {
    skip: !isEnglish, // Skip experiment and exposure if its not english
  });

  // Joy Stays detection - memoized to avoid recalculation
  const isJoyStays = useMemo(
    () => accommodation?.type === AccommodationCombinedType.Hotel && !accommodation?.isCustomLinkEnabled && accommodation?.isFromAccommodationCatalog,
    [accommodation],
  );

  // Link state calculations - grouped together for better performance
  const linkState = useMemo(() => {
    if (!accommodation) {
      return { hasLink: false, hasHotelLink: false, hasBookingLink: false };
    }

    const hasLink = hasAccommodationLink(accommodation.isCustomLinkEnabled, accommodation.customUrl);
    const isRoomBlock = accommodation.type === AccommodationCombinedType.Roomblock;
    const hasHotelLink = hasLink && !isRoomBlock;
    const hasBookingLink = isRoomBlock && !!accommodation.bookingUrl;

    return { hasLink, hasHotelLink, hasBookingLink };
  }, [accommodation]);

  const accommodationsPrices = useAccommodationsPrices(accommodation);
  // Track telemetry only for joy hotels
  const isHotelAvailable = isJoyStays ? !accommodationsPrices?.soldOutUrl : undefined;

  // URL parameter building helper - eliminates duplication and strips default properties
  const buildUrlParams = useCallback(() => {
    return stripDefaultProperties(
      {
        ...searchParams,
        [JoyHotelQueryStringParameters.joyHotelDiscountRule]: joyHotelDiscount?.rule || searchParams[JoyHotelQueryStringParameters.joyHotelDiscountRule],
        [JoyHotelQueryStringParameters.joyHotelDiscountCode]: joyHotelDiscount?.code || searchParams[JoyHotelQueryStringParameters.joyHotelDiscountCode],
      },
      [undefined, ''],
    ); // Add [undefined, ''] to strip both undefined and empty strings
  }, [searchParams, joyHotelDiscount]);

  // Consolidate telemetry data to avoid inline object creation
  const telemetryData = useMemo(
    () => ({
      hotelInfo: {
        hotelName: accommodation?.displayName || '',
        isHotelPreferred: accommodation?.isFavorite || false,
        hotelPrice: accommodation?.pricePerNight,
        hotelStrikeThroughPrice: accommodation?.strikeoutPricePerNight,
        accommodationsType: accommodation?.type,
        from: from || 'not_specified',
      },
      location: {
        lat: locationInfo?.latitude || eventInfo?.locationInfo?.latitude,
        lng: locationInfo?.longitude || eventInfo?.locationInfo?.longitude,
      },
      venueName: locationInfo?.name ?? eventInfo?.locationInfo?.name ?? '',
    }),
    [accommodation, locationInfo, eventInfo, from],
  );

  // CTA title logic - simplified from callback pattern
  const ctaTitle = useMemo(() => {
    if (!accommodation) return tq`See Availability`;
    if (accommodationsPrices?.soldOutUrl) return tq`Explore Hotels Nearby`;
    if (isEnglish && payload && isJoyStays) return (payload as { buttonText: string })?.buttonText;
    return accommodation.type === AccommodationCombinedType.Custom ? tq`Select` : tq`See Availability`;
  }, [accommodation, accommodationsPrices?.soldOutUrl, isEnglish, payload, isJoyStays, tq]);

  // URL building with priority: roomblock > sold-out > default accommodation place
  const ctaUrl = useMemo(() => {
    if (!accommodation?.id || !eventHandle) return '';

    const urlParams = buildUrlParams();

    // Priority 1: Roomblock routing
    if (accommodation.type === AccommodationCombinedType.Roomblock) {
      if (!accommodation.bookingUrl) return '';
      return hotelRoutePaths.roomblock.buildRelativeUrl(eventHandle, accommodation.id, urlParams);
    }

    // Priority 2: Sold-out URL (alternative hotels)
    if (accommodationsPrices?.soldOutUrl) {
      return applyUtmParametersToUrl(accommodationsPrices.soldOutUrl, mergedUtmParams || {});
    }

    // Priority 3: Default accommodation place URL
    return hotelRoutePaths.accommodationPlace.buildRelativeUrl(eventHandle, accommodation.id, urlParams);
  }, [accommodation, eventHandle, buildUrlParams, accommodationsPrices?.soldOutUrl, hotelRoutePaths.roomblock, hotelRoutePaths.accommodationPlace, mergedUtmParams]);

  const handleOnSelectAccommodation = useEventCallback((source?: string) => {
    if (!accommodation) return;

    if (accommodation.type === AccommodationCombinedType.Roomblock) {
      handleOnSelectRoomBlock();
      return;
    }
    handleOnSelectHotel(source);
  });

  const handleOnSelectHotel = useEventCallback((source?: string) => {
    if (!accommodation?.id || !eventHandle) return;

    const distance = distanceInMiles({ latitude: accommodation.latitude, longitude: accommodation.longitude }, telemetryData.location);

    const hotel = {
      eventHandle,
      accommodationPlaceId: accommodation.id,
      hotelName: accommodation.displayName || '',
      venueName: telemetryData.venueName,
      ...(distance && { milesFromVenue: distance.toFixed(1) }),
    };

    telemetry.viewHotelPDPPage(hotel, accommodationsPrices, isHotelAvailable);
    telemetry.accommodationsLinkInteracted(ctaUrl, telemetryData.hotelInfo, source);
  });

  const onSoldOutItemClick = useCallback(() => {
    if (accommodationsPrices?.soldOutUrl && isAccommodationsConsoleEnabled) {
      onSoldOutItemClickAction?.();
    }
  }, [accommodationsPrices?.soldOutUrl, isAccommodationsConsoleEnabled, onSoldOutItemClickAction]);

  const handleOnSelectRoomBlock = useEventCallback(() => {
    if (!accommodation?.id || !eventHandle) return;

    incrementInterestedGuestCount({
      variables: { reservedRoomBlockId: accommodation.id },
    });

    const telemetryHotelInfoWithInterestCount = {
      ...telemetryData.hotelInfo,
      displayedInterestedGuestCount: accommodation.interestedGuestCount || 0,
    };

    const urlParams = buildUrlParams();
    telemetry.accommodationsLinkInteracted(hotelRoutePaths.roomblock.buildRelativeUrl(eventHandle, accommodation.id, urlParams), telemetryHotelInfoWithInterestCount);
  });

  // Link enabled logic - simplified for readability
  const isLinkEnabled = useMemo(() => {
    const hasValidLink = linkState.hasBookingLink || linkState.hasHotelLink;
    if (!hasValidLink) return false;

    const isSoldOut = !!accommodationsPrices?.soldOutUrl;
    if (!isSoldOut) return true;

    // Sold out: only enable if accommodations console is disabled
    return !isAccommodationsConsoleEnabled;
  }, [linkState, accommodationsPrices?.soldOutUrl, isAccommodationsConsoleEnabled]);

  // Button visibility - based on link availability
  const shouldShowButton = linkState.hasBookingLink || linkState.hasHotelLink;

  return {
    ctaTitle,
    ctaUrl,
    ctaVariant: accommodationsPrices?.soldOutUrl ? 'outline' : 'solid',
    isLinkEnabled,
    shouldShowButton,
    handleOnSelectAccommodation,
    onSoldOutItemClick,
  };
};
